import type * as utils from '@reolink-web/utils';
export interface ISupportLangItem {
    title: string;
    value: string;
}

export interface ISupportCountryItem {
    continentTitle: string;
    continentCode: string;
    countryList: Array<{
        value: string;
        title: string;
        lang: string;
        langKey: string;
        currency: string;
    }>;
}

/**
 * 官网支持的语言列表
 */
export const supportLangList: ISupportLangItem[] = [
    {
        title: 'English',
        value: 'en',
    },
    {
        title: 'Deutsch',
        value: 'de',
    },
    {
        title: 'Français',
        value: 'fr',
    },
    {
        title: 'Italiano',
        value: 'it',
    },
    {
        title: 'Español',
        value: 'es',
    },
    {
        title: '日本語',
        value: 'jp',
    },
    {
        title: 'Português',
        value: 'pt',
    },
    {
        title: 'Polski',
        value: 'pl',
    },
    {
        title: 'Nederlands',
        value: 'nl',
    },
    {
        title: 'Svenska',
        value: 'sv',
    },
];

/**
 * 支持的语言映射
 */
export const supportLangMap: Record<string, string> = {
    'en-US': 'English',
    en: 'English',
    de: 'Deutsch',
    fr: 'Français',
    it: 'Italiano',
    es: 'Español',
    jp: '日本語',
    pt: 'Português',
    pl: 'Polski',
    nl: 'Nederlands',
    sv: 'Svenska',
};

/**
 * 对语言做一层处理
 * @param code
 * @returns
 */
export const langCodeTransform = (code: string): string => {
    switch (code) {
        case 'en-US':
            return 'en';
        case 'ja':
            return 'jp';
        default:
            return code;
    }
};

interface IContinentItem {
    code: string;
    name: Record<string, string>;
}

/**
 * 洲信息
 */
export const continentList: IContinentItem[] = [
    {
        code: 'NA',
        name: {
            en: 'North America',
            fr: 'Amérique du Nord',
            de: 'Nordamerika',
            it: 'Nord America',
            es: 'Norteamérica',
            jp: '北米',
            pt: 'América do Norte',
            ru: 'Северная Америка',
            pl: 'Ameryka Północna',
            nl: 'Noord-Amerika',
            sv: 'Nordamerika',
        },
    },
    {
        code: 'EU',
        name: {
            en: 'Europe',
            fr: 'Europe',
            de: 'Europa',
            it: 'Europa',
            es: 'Europa',
            jp: 'ヨーロッパ',
            pt: 'Europa',
            ru: 'Европа',
            pl: 'Europa',
            nl: 'Europa',
            sv: 'Europa',
        },
    },
    {
        code: 'OC',
        name: {
            en: 'Oceania',
            fr: 'Océanie',
            de: 'Ozeanien',
            it: 'Oceania',
            es: 'Oceanía',
            jp: 'オセアニア',
            pt: 'Oceânia',
            ru: 'Океания',
            pl: 'Oceania',
            nl: 'Oceanië',
            sv: 'Oceanien',
        },
    },
    {
        code: 'SA',
        name: {
            en: 'Central & South America',
            fr: 'Amérique centrale et du Sud',
            de: 'Zentral- & Südamerika',
            it: 'Centro e Sud America',
            es: 'América Central y del Sur',
            jp: 'Central & South America',
            pt: 'América Central e do Sul',
            ru: 'Central & South America',
            pl: 'Ameryka Środkowa i Południowa',
            nl: 'Centraal- en Zuid-Amerika',
            sv: 'Central- och Sydamerika',
        },
    },
    {
        code: 'AS',
        name: {
            en: 'Asia-Pacific',
            fr: 'Asie-Pacifique',
            de: 'Asien-Pazifik',
            it: 'Asia-Pacifico',
            es: 'Asia-Pacífico',
            jp: 'Asia-Pacific',
            pt: 'Ásia-Pacífico',
            ru: 'Asia-Pacific',
            pl: 'Azja-Pacyfik',
            nl: 'Azië-Pacific',
            sv: 'Asien-Stillahavsområdet',
        },
    },
    {
        code: 'AF',
        name: {
            en: 'Middle East & Africa',
            fr: 'Moyen-Orient et Afrique',
            de: 'Naher Osten & Afrika',
            it: 'Medio Oriente e Africa',
            es: 'Oriente Medio y África',
            jp: 'Middle East & Africa',
            pt: 'Médio Oriente e África',
            ru: 'Middle East & Africa',
            pl: 'Bliski Wschód i Afryka',
            nl: 'Midden-Oosten en Afrika',
            sv: 'Mellanöstern och Afrika',
        },
    },
];

/**
 * 洲信息映射
 */
export const continentMap: Record<string, IContinentItem> = Object.fromEntries(
    continentList.map((item) => [item.code, item]),
);

/**
 * 设置国家的cookie key
 */
export const COUNTRY_COOKIE_KEY = 'REO_COUNTRY';

/**
 * 设置语言的cookie key
 */
export const LANGUAGE_COOKIE_KEY = 'REO_LANGUAGE';

/**
 * 默认国家
 */
export const defaultCountry = 'US';
/**
 * 默认语言
 */
export const defaultLang = 'en';

/**
 * 产品加入购物车后触发的事件key
 * 挂载在body上
 */
export const AFTER_ADD_TO_CART_EVENT_KEY = 'afterAddToCart';

export interface ICountryLangEvent {
    countryLangChange: {
        lang: string;
        country: string;
        countryTitle: string;
        langTitle: string;
        countryList: ISupportCountryItem[];
        isDistributor: boolean;
    };
    countryLangModalOpen: utils.HelperTypes.IEmptyObject;
}

export type IEventMap = ICountryLangEvent;
